import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { APP_ROUTING } from '../app-routing.constants';
import { ErrorCommunicationService } from './error-communication.service';

@Injectable({
  providedIn: 'root',
})
export class CrudService {
  ENV = environment.apiUrl;
  ENV_LOCAL = environment.apiUrlLocal;

  constructor(
    private router: Router,
    private http: HttpClient,
    private errorCommunicationService: ErrorCommunicationService,
  ) {}

  async getLocal(endPointUrl: string, options?: any): Promise<any> {
    try {
      return await firstValueFrom(this.http.get(this.ENV_LOCAL + endPointUrl, options));
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  async get(endPointUrl: string, options?: any): Promise<any> {
    try {
      return await firstValueFrom(this.http.get(this.ENV + endPointUrl, options));
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  async post(endPointUrl: string, options?: any): Promise<any> {
    try {
      return await firstValueFrom(this.http.post(this.ENV + endPointUrl, options));
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  async postFormData(endPointUrl: string, formData: FormData): Promise<any> {
    try {
      const options = {};
      return await firstValueFrom(this.http.post(this.ENV + endPointUrl, formData, options));
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  async patchFormData(endPointUrl: string, formData: FormData): Promise<any> {
    try {
      const options = {};
      return await firstValueFrom(this.http.patch(this.ENV + endPointUrl, formData, options));
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }
  async delete(endPointUrl: string, options?: any): Promise<any> {
    try {
      return await firstValueFrom(this.http.delete(this.ENV + endPointUrl, options));
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  async update(endPointUrl: string, options?: any): Promise<any> {
    try {
      return await firstValueFrom(this.http.patch(this.ENV + endPointUrl, options));
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  async put(endPointUrl: string, options?: any): Promise<any> {
    try {
      return await firstValueFrom(this.http.put(this.ENV + endPointUrl, options));
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  private handleError(error: any): void {
    const apiErrorMessages = {
      '400': 'The information you provided is invalid. Please check and try again.',
      '401':
        'You are not authorized to access this resource. Please log in or check your permissions.',
      '403': 'You do not have permission to access this resource.',
      '404': 'The resource you requested could not be found.',
      '405': 'The method used to access the resource is not allowed.',
      '408': 'The request timed out. Please try again later.',
      '409': 'There was a conflict with your request. Please try again.',
      '413': 'The file you uploaded is too large. Please upload a smaller file.',
      '429': 'Too many requests. Please try again later.',
      '500': 'An unexpected error occurred. Please try again later.',
      '502': 'The server is currently unavailable. Please try again later.',
      '503': 'The service is temporarily unavailable. Please try again later.',
      '504': 'The server took too long to respond. Please try again later.',
      '505': 'The server does not support the requested protocol.',
      '520': 'An unknown error occurred on the server.',
      '521': 'The server is down or unreachable.',
      '522': 'The connection to the server timed out.',
      '523': 'The server is unreachable.',
      '524': 'A timeout occurred.',
      '525': 'SSL handshake failed.',
      '526': 'Invalid SSL certificate.',
    };
    let errorMessage: string = 'An error occurred';

    if (error instanceof HttpErrorResponse) {
      if (error.error instanceof ErrorEvent) {
        errorMessage = `Error: ${error.error.message}`;
      } else {
        let status: string = `${error.status}`;
        errorMessage =
          apiErrorMessages[status as keyof typeof apiErrorMessages] ?? 'An error occurred';
        // Log the actual server response for debugging validation errors
        if (error.error) {
          console.error('Server response:', error.error);
          if (error.error.message) {
            const serverMsg = Array.isArray(error.error.message)
              ? error.error.message.join(', ')
              : error.error.message;
            errorMessage = serverMsg;
          }
        }
        if (error.status === 403) {
          this.router.navigate([APP_ROUTING['ACCESS_DENIED']]);
        }
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    this.errorCommunicationService.showError(errorMessage);
    console.error(errorMessage);
  }
  getBaseUrl(): string {
    return this.ENV;
  }
}
